import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { TrendBand, CHART } from '../components/analytics/kit';

const Donations = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const res = await api.get('/donations');
        setDonations(res.data.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchDonations();
  }, []);

  const filteredDonations = donations
    .filter(d => {
      const matchesSearch = 
        d.donorName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        d.donorEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.razorpayPaymentId?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'date_asc') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'amount_desc') return b.amount - a.amount;
      if (sortBy === 'amount_asc') return a.amount - b.amount;
      return 0;
    });

  return (
    <div className="p-8">
      <TrendBand docs={donations} amountKey="amount" title="Donation Flow · Live" accent={CHART.gold} />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="font-serif text-3xl font-bold text-[#2A2858]">Donation Records</h2>
          <p className="mt-1 text-stone-500 text-sm">Track sacred contributions and 80G receipts</p>
        </div>
        
        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">search</span>
            <input 
              type="text" 
              placeholder="Search donor or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B]"
            />
          </div>
          
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
          >
            <option value="all">All Status</option>
            <option value="SUCCESS">Success</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
          </select>

          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
          >
            <option value="date_desc">Newest First</option>
            <option value="date_asc">Oldest First</option>
            <option value="amount_desc">Highest Amount</option>
            <option value="amount_asc">Lowest Amount</option>
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#E8B84B]/20 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-20 flex justify-center"><div className="w-8 h-8 border-4 border-[#2A2858]/20 border-t-[#2A2858] rounded-full animate-spin"></div></div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-[#FAF9F6] border-b border-[#E8B84B]/20 text-xs font-bold text-[#2A2858] uppercase tracking-widest">
                <th className="px-6 py-4">Donor Name</th>
                <th className="px-6 py-4">Centre</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filteredDonations.map((d) => (
                <tr key={d._id} className="hover:bg-stone-50">
                  <td className="px-6 py-4">
                    <div className="font-bold text-stone-800">{d.donorName}</div>
                    <div className="text-[10px] text-[#E56B2E] font-bold uppercase">{d.offeringName || 'General Donation'}</div>
                    <div className="text-[10px] text-stone-400">{d.donorEmail}</div>
                  </td>
                  <td className="px-6 py-4 text-stone-600">{d.centreId?.name || 'N/A'}</td>
                  <td className="px-6 py-4 font-bold text-green-600">₹{d.amount}</td>
                  <td className="px-6 py-4">
                    <div className="text-stone-700">{new Date(d.createdAt).toLocaleDateString()}</div>
                    <div className="text-[10px] text-stone-400 font-mono">{d.razorpayPaymentId || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold border uppercase ${
                      d.status === 'SUCCESS' ? 'bg-green-50 text-green-700 border-green-200' : 
                      d.status === 'FAILED' ? 'bg-red-50 text-red-700 border-red-200' : 
                      'bg-amber-50 text-amber-700 border-amber-200'
                    }`}>
                      {d.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filteredDonations.length === 0 && (
                <tr><td colSpan="5" className="px-6 py-10 text-center text-stone-400">No donations found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Donations;